import {
	ChangeEvent,
	ClipboardEvent,
	forwardRef,
	KeyboardEvent,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';
import { lazily } from 'react-lazily';

import styles from './six-digits-code-form.module.scss';

const { Insert, Error } = lazily(() => import('core/generic/elements/form'));

export interface SixDigitsCodeFormHandle {
	reset: () => void;
	showErrorMessage: (message: string) => void;
}

export type SixDigitsCodeFormProps = {
	outputCode: (code: string) => void;
};

const CODE_LENGTH = 6;

const SixDigitsCodeFormComponent: React.ForwardRefRenderFunction<
	SixDigitsCodeFormHandle,
	SixDigitsCodeFormProps
> = ({ outputCode }, ref) => {
	const [errorMessage, setErrorMessage] = useState('');
	const [otp, setOtp] = useState<string[]>(new Array(CODE_LENGTH).fill(''));
	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

	useEffect(() => {
		if (inputRefs.current[0]) {
			inputRefs.current[0]?.focus();
		}
	}, []);

	useEffect(() => {
		outputCode(otp.join(''));
	}, [otp]);

	const resetInputs = () => {
		setOtp(new Array(CODE_LENGTH).fill(''));
		if (inputRefs.current[0]) {
			inputRefs.current[0]?.focus();
		}
	};

	const showErrorMessage = (message: string) => {
		setErrorMessage(message);
	};

	useImperativeHandle(ref, () => ({
		reset: resetInputs,
		showErrorMessage,
	}));

	const handleChange = (
		event: ChangeEvent<HTMLInputElement>,
		index: number
	) => {
		const { value } = event.target;
		if (/[^0-9]/.test(value)) return;

		setErrorMessage('');
		const newOtp = [...otp];
		newOtp[index] = value.slice(-1);
		setOtp(newOtp);

		if (value && index < CODE_LENGTH - 1 && inputRefs.current[index + 1]) {
			inputRefs.current[index + 1]?.focus();
		}
	};

	const handleKeyDown = (
		event: KeyboardEvent<HTMLInputElement>,
		index: number
	) => {
		if (event.key === 'Backspace') {
			if (!otp[index] && index > 0 && inputRefs.current[index - 1]) {
				inputRefs.current[index - 1]?.focus();
				const newOtp = [...otp];
				newOtp[index - 1] = '';
				setOtp(newOtp);
			}
		}
	};

	const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
		event.preventDefault();
		const pastedData = event.clipboardData
			.getData('text/plain')
			.slice(0, CODE_LENGTH);

		if (/[^0-9]/.test(pastedData)) return;

		const newOtp = [...otp];
		const activeElement = document.activeElement as HTMLInputElement;
		let currentInputIndex = inputRefs.current.findIndex(
			(input) => input === activeElement
		);
		if (currentInputIndex === -1) {
			currentInputIndex = 0;
		}

		for (
			let i = 0;
			i < pastedData.length && currentInputIndex + i < CODE_LENGTH;
			i++
		) {
			newOtp[currentInputIndex + i] = pastedData[i];
		}

		setOtp(newOtp);

		const nextIndex = currentInputIndex + pastedData.length;
		if (nextIndex < CODE_LENGTH && inputRefs.current[nextIndex]) {
			inputRefs.current[nextIndex]?.focus();
		} else if (inputRefs.current[CODE_LENGTH - 1]) {
			inputRefs.current[CODE_LENGTH - 1]?.focus();
		}
	};

	const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
		event.target.select();
	};

	return (
		<>
			<div className={styles['input-wrapper']} role="group">
				{otp.map((value, index) => (
					<Insert
						key={`code${index}`}
						type="code"
						value={value}
						maxLength={1}
						id={`code${index}`}
						label=""
						required
						disabled={false}
						autoFocus={index === 0}
						error={errorMessage && ' '}
						aria-label={`Digit ${index + 1} of 6`}
						onChange={(event) => handleChange(event, index)}
						onKeyDown={(event) => handleKeyDown(event, index)}
						ref={(el) => {
							inputRefs.current[index] = el;
						}}
						onPaste={handlePaste}
						onFocus={handleFocus}
					/>
				))}
			</div>
			{errorMessage && (
				<Error text={errorMessage} icon="AlertCircleFilled" />
			)}
		</>
	);
};

const SixDigitsCodeForm = forwardRef(SixDigitsCodeFormComponent);
export default SixDigitsCodeForm;
